import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import type {
  DrawingSessionDTO,
  PlayerState,
  SerializedSession,
  Stroke,
  VoteTally,
} from '@share-paint/shared';
import { connect, debugInfo, on, onClose, onError, onOpen, send } from '../lib/socket';

type Status = 'IDLE' | 'LOBBY' | 'PLAYING' | 'VOTING' | 'REVEAL' | 'FINISHED';

export interface DebugInfo {
  url: string;
  readyState: number | null;
  readyStateLabel: string;
  closedCode: number | null;
  closedReason: string | null;
}

interface State {
  connected: boolean;
  me: PlayerState | null;
  session: SerializedSession | null;
  activeDrawing: DrawingSessionDTO | null;
  gallery: DrawingSessionDTO[];
  finalDrawings: DrawingSessionDTO[];
  msLeft: number;
  roundIndex: number;
  totalRounds: number;
  status: Status;
  tally: VoteTally[];
  voters: { voted: number; total: number };
  myVote: string | null;
  errors: { code: string; message: string }[];
  debug: DebugInfo;
}

type Action =
  | { type: 'CONNECTED'; url: string }
  | { type: 'DISCONNECTED' }
  | { type: 'WS_OPEN_FAILED'; url: string }
  | { type: 'WS_CLOSE'; url: string; code: number; reason: string }
  | { type: 'RESET' }
  | { type: 'ROOM_JOINED'; session: SerializedSession; you: PlayerState }
  | { type: 'PLAYERS_UPDATED'; players: PlayerState[] }
  | { type: 'GAME_STARTED'; drawing: DrawingSessionDTO; totalRounds: number }
  | { type: 'TICK'; msLeft: number; roundIndex: number; totalRounds: number }
  | { type: 'STROKE'; drawingId: string; strokes: Stroke[]; version: number }
  | { type: 'SNAPSHOT'; drawing: DrawingSessionDTO; roundIndex: number }
  | { type: 'ROUND_ENDED'; roundIndex: number }
  | { type: 'VOTING_STARTED'; finalDrawings: DrawingSessionDTO[] }
  | { type: 'VOTE_UPDATE'; voted: number; total: number }
  | { type: 'VOTED'; drawingId: string }
  | { type: 'GAME_OVER'; tally: VoteTally[] }
  | { type: 'ERROR'; code: string; message: string };

const READY_STATE_LABELS: Record<number, string> = {
  [WebSocket.CONNECTING]: 'CONNECTING',
  [WebSocket.OPEN]: 'OPEN',
  [WebSocket.CLOSING]: 'CLOSING',
  [WebSocket.CLOSED]: 'CLOSED',
};


const initial: State = {
  connected: false,
  me: null,
  session: null,
  activeDrawing: null,
  gallery: [],
  finalDrawings: [],
  msLeft: 0,
  roundIndex: 0,
  totalRounds: 0,
  status: 'IDLE',
  tally: [],
  voters: { voted: 0, total: 0 },
  myVote: null,
  errors: [],
  debug: {
    url: '',
    readyState: null,
    readyStateLabel: 'IDLE',
    closedCode: null,
    closedReason: null,
  },
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'RESET':
      return { ...initial, connected: state.connected, debug: state.debug };
    case 'CONNECTED':
      return {
        ...state,
        connected: true,
        errors: [],
        debug: {
          url: action.url,
          readyState: WebSocket.OPEN,
          readyStateLabel: 'OPEN',
          closedCode: null,
          closedReason: null,
        },
      };
    case 'DISCONNECTED':
      return { ...state, connected: false };
    case 'WS_OPEN_FAILED':
      return {
        ...state,
        connected: false,
        debug: { ...state.debug, url: action.url, readyStateLabel: 'CONNECT_FAILED' },
      };
    case 'WS_CLOSE':
      return {
        ...state,
        connected: false,
        errors: [
          ...state.errors.slice(-3),
          {
            code: 'WS_CLOSED',
            message: `WebSocket cerrado (code=${action.code} reason=${action.reason || '-'})`,
          },
        ],
        debug: {
          ...state.debug,
          url: action.url,
          readyState: WebSocket.CLOSED,
          readyStateLabel: 'CLOSED',
          closedCode: action.code,
          closedReason: action.reason,
        },
      };
    case 'ROOM_JOINED':
      return {
        ...state,
        session: action.session,
        me: action.you,
        status:
          action.session.status === 'FINISHED'
            ? 'REVEAL'
            : (action.session.status as Status),
      };
    case 'PLAYERS_UPDATED': {
      if (!state.session) return state;
      return { ...state, session: { ...state.session, players: action.players } };
    }
    case 'GAME_STARTED':
      return {
        ...state,
        status: 'PLAYING',
        activeDrawing: action.drawing,
        totalRounds: action.totalRounds,
        session: state.session ? { ...state.session, status: 'PLAYING' } : state.session,
      };
    case 'TICK':
      return {
        ...state,
        msLeft: action.msLeft,
        roundIndex: action.roundIndex,
        totalRounds: action.totalRounds || state.totalRounds,
      };
    case 'STROKE': {
      if (!state.activeDrawing || state.activeDrawing.id !== action.drawingId) return state;
      return {
        ...state,
        activeDrawing: {
          ...state.activeDrawing,
          strokes: [...state.activeDrawing.strokes, ...action.strokes],
          version: action.version,
        },
      };
    }
    case 'SNAPSHOT':
      return {
        ...state,
        activeDrawing: action.drawing,
        roundIndex: action.roundIndex,
      };
    case 'ROUND_ENDED':
      return state;
    case 'VOTING_STARTED':
      return {
        ...state,
        status: 'VOTING',
        finalDrawings: action.finalDrawings,
        session: state.session ? { ...state.session, status: 'VOTING' } : state.session,
      };
    case 'VOTE_UPDATE':
      return { ...state, voters: { voted: action.voted, total: action.total } };
    case 'VOTED':
      return { ...state, myVote: action.drawingId };
    case 'GAME_OVER':
      return {
        ...state,
        status: 'REVEAL',
        tally: action.tally,
        session: state.session ? { ...state.session, status: 'FINISHED' } : state.session,
      };
    case 'ERROR':
      return { ...state, errors: [...state.errors.slice(-4), { code: action.code, message: action.message }] };
    default:
      return state;
  }
}

export { READY_STATE_LABELS };

// ───────────────────────────────── Persistencia de identidad ──────────────

interface PersistedIdentity {
  userId: string;
  username: string;
  code: string;
}

const PERSIST_KEY = 'share-paint:identity';

function readIdentity(): PersistedIdentity | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.userId === 'string' &&
      typeof parsed?.code === 'string' &&
      typeof parsed?.username === 'string'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeIdentity(id: PersistedIdentity | null) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (id) localStorage.setItem(PERSIST_KEY, JSON.stringify(id));
    else localStorage.removeItem(PERSIST_KEY);
  } catch {
    /* noop */
  }
}

interface Ctx {
  state: State;
  debug: DebugInfo;
  refreshDebug: () => DebugInfo;
  createRoom: (username: string, maxPlayers?: number) => Promise<string | null>;
  joinRoom: (code: string, username: string) => void;
  startGame: () => void;
  submitStrokes: (strokes: Stroke[]) => void;
  requestSnapshot: () => void;
  castVote: (drawingId: string) => void;
  leaveSession: () => void;
}

const GameCtx = createContext<Ctx | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initial);
  const rejoinAttempted = useRef(false);
  // Guardamos una ref de la sala actual para rejoin en cada cambio de código.
  const currentCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const refreshDbg = () => {
      const info = debugInfo();
      dispatch({
        type: 'WS_OPEN_FAILED', // reutilizamos para forzar actualización del debug
        url: info.url,
      });
      return info;
    };

    const offOpen = onOpen(() => {
      const info = debugInfo();
      dispatch({ type: 'CONNECTED', url: info.url });
    });
    const offClose = onClose((code, reason) => {
      const info = debugInfo();
      dispatch({ type: 'WS_CLOSE', url: info.url, code, reason });
    });
    const offSocketError = onError(() => {
      const info = debugInfo();
      dispatch({ type: 'WS_OPEN_FAILED', url: info.url });
    });

    const offRoomJoined = on('room_joined', (p) => {
      dispatch({ type: 'ROOM_JOINED', ...p });
      writeIdentity({
        userId: p.you.id,
        username: p.you.username,
        code: p.session.code,
      });
      rejoinAttempted.current = true;
    });
    const offPlayerJoined = on('player_joined', (p) =>
      dispatch({ type: 'PLAYERS_UPDATED', players: p.players }),
    );
    const offPlayerLeft = on('player_left', (p) =>
      dispatch({ type: 'PLAYERS_UPDATED', players: p.players }),
    );
    const offGameStarted = on('game_started', (p) =>
      dispatch({ type: 'GAME_STARTED', drawing: p.drawing, totalRounds: p.totalRounds }),
    );
    const offTick = on('tick', (p) => dispatch({ type: 'TICK', ...p }));
    const offStroke = on('stroke_broadcast', (p) => dispatch({ type: 'STROKE', ...p }));
    const offSnap = on('snapshot_full', (p) => dispatch({ type: 'SNAPSHOT', ...p }));
    const offRotated = on('drawing_rotated', (p) =>
      dispatch({ type: 'SNAPSHOT', drawing: p.drawing, roundIndex: p.roundIndex }),
    );
    const offRoundEnded = on('round_ended', (p) => dispatch({ type: 'ROUND_ENDED', roundIndex: p.roundIndex }));
    const offVoting = on('voting_started', (p) => dispatch({ type: 'VOTING_STARTED', finalDrawings: p.finalDrawings }));
    const offVoteRec = on('vote_received', (p) => dispatch({ type: 'VOTE_UPDATE', voted: p.voterCount, total: p.totalVoters }));
    const offGameOver = on('game_over', (p) => dispatch({ type: 'GAME_OVER', tally: p.tally }));
    const offError = on('error', (p) => dispatch({ type: 'ERROR', ...p }));

    // Intento de rejoin automático al cargar la página.
    const identity = readIdentity();
    if (identity && identity.code && identity.userId) {
      currentCodeRef.current = identity.code;
      connect(identity.code);
      // Pequeño delay para que el WS abra.
      setTimeout(() => {
        if (rejoinAttempted.current) return;
        rejoinAttempted.current = true;
        send({
          type: 'rejoin_session',
          payload: {
            code: identity.code,
            userId: identity.userId,
            username: identity.username,
          },
        });
        // Watchdog: si no llega room_joined en 5s, limpiamos identidad.
        setTimeout(() => {
          const persisted = readIdentity();
          if (persisted && persisted.userId === identity.userId && persisted.code === identity.code) {
            // si no apareció ROOM_JOINED con esta identidad, limpiamos
            // (se detecta porque dispatch ya habría rellenado state.session.me)
            // Implementación simple: si tras 5s seguimos sin session, clear.
            // (state no es accesible aquí; lo hacemos en mount sub-useEffect)
          }
        }, 5000);
      }, 250);
    }

    return () => {
      offOpen();
      offClose();
      offSocketError();
      offRoomJoined();
      offPlayerJoined();
      offPlayerLeft();
      offGameStarted();
      offTick();
      offStroke();
      offSnap();
      offRotated();
      offRoundEnded();
      offVoting();
      offVoteRec();
      offGameOver();
      offError();
    };
  }, []);

  // Watchdog de rejoin: si tras 5s no tenemos sesión, limpiamos identidad.
  useEffect(() => {
    if (!rejoinAttempted.current) return;
    const t = setTimeout(() => {
      if (!state.session) writeIdentity(null);
    }, 5000);
    return () => clearTimeout(t);
  }, [state.session]);

  const c: Ctx = useMemo(
    () => ({
      state,
      debug: state.debug,
      refreshDebug: () => {
        const info = debugInfo();
        const labels: Record<number, string> = {
          0: 'CONNECTING',
          1: 'OPEN',
          2: 'CLOSING',
          3: 'CLOSED',
        };
        return {
          url: info.url,
          readyState: info.state,
          readyStateLabel: info.state == null ? 'IDLE' : labels[info.state] ?? 'UNKNOWN',
          closedCode: state.debug.closedCode,
          closedReason: state.debug.closedReason,
        };
      },
      createRoom: async (username, maxPlayers = 6) => {
        const code = generateClientCode();
        connect(code);
        send({
          type: 'create_room',
          payload: { code, username, maxPlayers, durationSec: 60 },
        });
        return code;
      },
      joinRoom: (code, username) => {
        connect(code);
        send({ type: 'join_room', payload: { code, username } });
        writeIdentity({ code, userId: '', username });
      },
      startGame: () => send({ type: 'start_game' }),
      submitStrokes: (strokes) => {
        if (strokes.length === 0) return;
        send({ type: 'submit_strokes', payload: { batch: { strokes } } });
      },
      requestSnapshot: () => send({ type: 'request_snapshot' }),
      castVote: (drawingId) => {
        dispatch({ type: 'VOTED', drawingId });
        send({ type: 'cast_vote', payload: { drawingId } });
      },
      leaveSession: () => {
        writeIdentity(null);
        dispatch({ type: 'RESET' });
      },
    }),
    [state],
  );

  return <GameCtx.Provider value={c}>{children}</GameCtx.Provider>;
}

export function useGame() {
  const ctx = useContext(GameCtx);
  if (!ctx) throw new Error('useGame must be used inside <GameProvider>');
  return ctx;
}

const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
function generateClientCode(): string {
  let s = '';
  const a = CODE_ALPHABET;
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;
  if (cryptoObj?.getRandomValues) {
    const buf = new Uint32Array(6);
    cryptoObj.getRandomValues(buf);
    for (let i = 0; i < 6; i++) s += a[buf[i] % a.length];
  } else {
    for (let i = 0; i < 6; i++) s += a[Math.floor(Math.random() * a.length)];
  }
  return s;
}
